// Generate code from clean architecture template

import * as yup from 'yup';
import { RankingConfigurationPayload } from '@/domains/config-system/ports/payloads';

// Define more specific types for form data
interface RankFormData {
  rank: string;
  min: number;
  max: number | null;
  id?: string;
}

type RankFormArray = RankFormData[];

export class SalesTargetRankingValidation {
  static createValidationSchema = () =>
    yup.object().shape({
      ranks: yup
        .array()
        .of(
          yup.object().shape({
            rank: yup.string().required('Rank is required'),
            min: yup.number().required('Min KPI is required').min(0, 'Min KPI must be at least 0'),
            max: yup
              .number()
              .nullable()
              .test(
                'min-max',
                'Max KPI must be greater than Min KPI',
                function (max: number | null | undefined) {
                  const { min } = this.parent;
                  if (max === null || max === undefined) return true; // null max is valid (infinity)
                  return Number(max) > Number(min);
                },
              ),
          }),
        )
        .test(
          'no-overlapping-ranges',
          'KPI ranges cannot overlap between different ranks',
          function (value) {
            const ranks = value as unknown as RankFormArray;
            if (!ranks || ranks.length <= 1) return true;

            // Sort ranks by min value for easier comparison
            const sortedRanks = [...ranks].sort((a, b) => (a.min ?? 0) - (b.min ?? 0));

            // Check for overlaps
            for (let i = 0; i < sortedRanks.length - 1; i++) {
              const currentRank = sortedRanks[i];
              const nextRank = sortedRanks[i + 1];

              // If current rank has no max (infinity), it will overlap with everything
              if (currentRank.max === null) {
                return this.createError({
                  path: 'ranks',
                  message: `Rank ${currentRank.rank} has no upper limit and will overlap with other ranks`,
                });
              }

              // Check if any value in the next rank's range falls within the current rank's range
              // For a range [min, max), a value is in the range if: value >= min && value < max
              if (
                (nextRank.min >= currentRank.min && nextRank.min < currentRank.max) || // Next rank's min falls in current range
                (nextRank.max !== null &&
                  nextRank.max > currentRank.min &&
                  nextRank.max <= currentRank.max) || // Next rank's max falls in current range
                (nextRank.min <= currentRank.min &&
                  nextRank.max !== null &&
                  nextRank.max > currentRank.max) // Next rank completely encompasses current rank
              ) {
                return this.createError({
                  path: 'ranks',
                  message: `Overlap detected between rank ${currentRank.rank} and ${nextRank.rank}`,
                });
              }
            }
            return true;
          },
        ),
    });

  static updateValidationSchema = () =>
    yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().nullable().optional(),
          rank: yup.string().required('Rank is required'),
          values: yup
            .array()
            .of(yup.number().required('Value is required'))
            .length(2, 'Values must contain exactly 2 elements')
            .test('min-max', 'Max KPI must be greater than Min KPI', function (values) {
              if (!values) return true;
              const [min, max] = values;
              if (max === null || max === undefined) return true;
              return max > (min ?? 0);
            })
            .required('Values are required'),
        }),
      )
      .test(
        'no-overlapping-ranges',
        'KPI ranges cannot overlap between different ranks',
        function (value) {
          const items = value as unknown as RankingConfigurationPayload[];
          if (!items || items.length <= 1) return true;

          // Sort ranks by min value for easier comparison
          const sortedItems = [...items].sort((a, b) => {
            const aMin = a.values?.[0] ?? 0;
            const bMin = b.values?.[0] ?? 0;
            return aMin - bMin;
          });

          // Check for overlaps
          for (let i = 0; i < sortedItems.length - 1; i++) {
            const currentItem = sortedItems[i];
            const nextItem = sortedItems[i + 1];

            const currentMin = currentItem.values?.[0] ?? 0;
            const currentMax = currentItem.values?.[1];
            const nextMin = nextItem.values?.[0] ?? 0;
            const nextMax = nextItem.values?.[1];

            // If current rank has no max (infinity), it will overlap with everything
            if (currentMax === null || currentMax === undefined) {
              return this.createError({
                path: '',
                message: `Rank ${currentItem.rank} has no upper limit and will overlap with other ranks`,
              });
            }

            // Check if any value in the next rank's range falls within the current rank's range
            // For a range [min, max), a value is in the range if: value >= min && value < max
            if (
              (nextMin >= currentMin && nextMin < currentMax) || // Next rank's min falls in current range
              (nextMax !== null && nextMax > currentMin && nextMax <= currentMax) || // Next rank's max falls in current range
              (nextMin <= currentMin && nextMax !== null && nextMax > currentMax) // Next rank completely encompasses current rank
            ) {
              return this.createError({
                path: '',
                message: `Overlap detected between rank ${currentItem.rank} and ${nextItem.rank}`,
              });
            }
          }
          return true;
        },
      );
}
